import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { Flex, Text } from 'components/ui';
import Chiclet from 'components/Chiclet';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import { productSelector } from 'selectors/page/product/productSelector';
import { localeSelector } from 'viewModel/selectors/locale/localeSelector';
import productActions from 'actions/ProductActions';
import localeUtils from 'utils/LanguageLocale';
import anaConsts from 'analytics/constants';
import ProsAndConsEN from 'components/ProductPage/RatingsAndReviews/ProsAndCons/locales/ProsAndCons_en_US';
import ProsAndConsFR from 'components/ProductPage/RatingsAndReviews/ProsAndCons/locales/ProsAndCons_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(ProsAndConsEN, ProsAndConsFR);

const NUMBER_OF_PROS_AND_CONS = 3;
const LIMIT_OF_REVIEWS = 6;
const PAGE = 0;

const getSentimentsOfType = (sentiments, type) => sentiments.filter(s => s.type === type);

const ProsAndCons = () => {
    const dispatch = useDispatch();
    const product = useSelector(productSelector, shallowEqual);
    const language = useSelector(localeSelector);
    const localization = { pros: getText('pros'), cons: getText('cons') };

    const [selectedId, setSelectedId] = useState(product.selectedSentiment || null);

    useEffect(() => {
        setSelectedId(product.selectedSentiment || null);
    }, [product.selectedSentiment]);

    const pros = getSentimentsOfType(product.sentiments || [], 'pros');
    const cons = getSentimentsOfType(product.sentiments || [], 'cons');

    const deselectSentiment = () => {
        dispatch(productActions.setSelectedSentiment(''));
        dispatch(productActions.removeHighlightedReviews());
    };

    const fireAnalytics = (proConType = '', sentiment = '') => {
        const actionLink = `reviews:ratings&reviews-filter:${proConType}`;
        const selectedFilter = proConType.replace(/([A-Z])/g, ' $1') + '=' + sentiment;
        const pageDetail = digitalData.page.attributes?.sephoraPageInfo?.pageName?.split(':')?.[1];

        pdpAnalytics.trackLinkClick({
            filterSelections: selectedFilter,
            selectedFilter: selectedFilter,
            actionInfo: actionLink,
            linkName: actionLink,
            eventStrings: anaConsts.Event.EVENT_71,
            pageDetail
        });
    };

    const handleChicletClick = (sentiment, type) => {
        if (selectedId !== sentiment) {
            setSelectedId(sentiment);
            dispatch(
                productActions.loadHighlightedReviews({
                    sentiment,
                    type,
                    productId: product?.productDetails?.productId,
                    limit: LIMIT_OF_REVIEWS,
                    page: PAGE,
                    language
                })
            );
            fireAnalytics(type, sentiment);
        } else {
            setSelectedId(null);
            deselectSentiment();
        }
    };

    const renderChiclet = (item, type) => (
        <Chiclet
            key={item.sentiment}
            variant='shadow'
            isActive={item.sentiment === selectedId}
            onClick={() => handleChicletClick(item.sentiment, type)}
            children={`${item.sentiment} (${item.count})`}
        />
    );

    const createList = ({ sentiments, type }) => sentiments.slice(0, NUMBER_OF_PROS_AND_CONS).map(item => renderChiclet(item, type));

    return (
        <Flex
            flexDirection='column'
            lineHeight='tight'
        >
            {pros.length > 0 && (
                <>
                    <Text
                        is='h3'
                        fontWeight='bold'
                        marginBottom={4}
                        children={localization.pros}
                    />
                    <Flex
                        flexWrap='wrap'
                        gap={2}
                    >
                        {createList({ sentiments: pros, type: 'pros' })}
                    </Flex>
                </>
            )}
            {cons.length > 0 && (
                <>
                    <Text
                        is='h3'
                        fontWeight='bold'
                        paddingTop={[4, 5]}
                        marginTop='auto'
                        marginBottom={4}
                        children={localization.cons}
                    />
                    <Flex
                        flexWrap='wrap'
                        gap={2}
                    >
                        {createList({ sentiments: cons, type: 'cons' })}
                    </Flex>
                </>
            )}
        </Flex>
    );
};

export default wrapFunctionalComponent(ProsAndCons, 'ProsAndCons');
